import type React from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { FloppyDiskIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  isLoading?: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
};

export const UnsavedChangesDialog: React.FC<Props> = ({
  isLoading = false,
  onCancel,
  onDiscard,
  onSave,
}) => {
  const { t } = useTranslationWithPrefix('components.unsavedChanges');

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onCancel();
  };

  return (
    <Dialog
      aria-describedby="unsaved-changes-description"
      aria-labelledby="unsaved-changes-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={true}
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(17, 17, 17, 0.48)',
        },
        '& .MuiDialog-paper': {
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.72)',
          borderRadius: '20px',
          boxShadow: '0 24px 72px rgba(0, 50, 84, 0.18), 0 8px 24px rgba(17, 17, 17, 0.12)',
          margin: 2,
          overflow: 'hidden',
          width: '100%',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5, pt: { sm: 4, xs: 3 }, px: { sm: 4, xs: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={theme => ({
              alignItems: 'center',
              backgroundColor: `${theme.colors.blue}12`,
              borderRadius: '14px',
              color: theme.colors.blue,
              display: 'flex',
              flex: '0 0 auto',
              height: 48,
              justifyContent: 'center',
              width: 48,
            })}
          >
            <WarningCircleIcon aria-hidden size={26} weight="duotone" />
          </Box>
          <Typography id="unsaved-changes-title" variant="f20Medium">
            {t('title')}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: { sm: 4, xs: 3 }, pl: { sm: 12, xs: 3 }, pr: { sm: 4, xs: 3 } }}>
        <Typography color="text.secondary" id="unsaved-changes-description" variant="f14Regular">
          {t('message')}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={theme => ({
          backgroundColor: theme.colors.gray5,
          borderTop: `1px solid ${theme.colors.stroke}`,
          justifyContent: 'space-between',
          px: { sm: 4, xs: 3 },
          py: 2.5,
        })}
      >
        <Button disabled={isLoading} onClick={handleClose} variant="text">
          {t('cancel')}
        </Button>
        <Stack direction="row" spacing={1.5}>
          <Button color="warning" disabled={isLoading} onClick={onDiscard} variant="text">
            {t('discard')}
          </Button>
          <Button
            loading={isLoading}
            onClick={onSave}
            startIcon={<FloppyDiskIcon aria-hidden size={18} weight="bold" />}
            variant="contained"
          >
            {t('save')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
